import { Button, IconButton } from '@chakra-ui/react';
import type { IconButtonProps } from '@chakra-ui/react';
import { NavArrowLeft } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

type BackIconButtonProps = Omit<IconButtonProps, 'aria-label'> & {
  'aria-label'?: string;
};

/**
 * The canonical round back affordance: a small outline icon button used by the
 * modal shell header and any detail surface that returns to its list. Icon-only,
 * no shadow — border and hover fill carry it.
 */
export const BackIconButton = ({ 'aria-label': ariaLabel, ...props }: BackIconButtonProps) => {
  const { t } = useTranslation();
  return (
    <IconButton
      aria-label={ariaLabel ?? t('common.back')}
      variant="outline"
      size="sm"
      rounded="full"
      boxSize="30px"
      minW="30px"
      flex="none"
      color="primary.400"
      borderColor="border"
      bg="transparent"
      _hover={{ bg: 'whiteSmoke', color: 'dark' }}
      {...props}
    >
      <NavArrowLeft width={17} height={17} />
    </IconButton>
  );
};

interface BackButtonProps {
  onClick: () => void;
  /** Where the button leads ("Brief templates"); icon only without it. */
  label?: string;
  'aria-label'?: string;
}

/**
 * The labelled back affordance of a page bar: a quiet arrow followed by the
 * name of the place it returns to. The page top nav and the editors' sticky
 * bars share it, so every "back to the list" reads the same.
 */
export const BackButton = ({ onClick, label, 'aria-label': ariaLabel }: BackButtonProps) => {
  const { t } = useTranslation();
  return (
    <Button
      aria-label={ariaLabel ?? label ?? t('common.back')}
      onClick={onClick}
      variant="ghost"
      size="sm"
      px="1.5"
      gap="1"
      flex="none"
      color="inkMuted"
      fontWeight="medium"
      _hover={{ bg: 'softCream', color: 'ink' }}
      _active={{ bg: 'softCream' }}
    >
      <NavArrowLeft width={17} height={17} />
      {label}
    </Button>
  );
};
